/**
 * Per-table sort state, stored in the URL so it can be shared and reloaded.
 *
 * The URL is the single source of truth: no duplicate copy in component state,
 * so the two can never drift apart when the user hits Back or pastes a link.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
// Imported from the pure logic module rather than the package barrel: this hook
// only needs the cycle function, not the whole React component library.
import {
  cycleSortState,
  type SortDirection,
  type SortState,
} from '@kioskgaming/ui/dist/data-table/sortState';

export type ColumnValueKind = 'money' | 'datetime' | 'ranked' | 'text';

export interface ColumnSortSpec {
  /** Sent to the backend as `sortBy`. Must match the endpoint whitelist (FR-025). */
  key: string;
  label: string;
  valueKind: ColumnValueKind;
  /** `true` when the column sorts by absolute value — the UI must say so (FR-009b). */
  magnitude?: boolean;
  defaultDirection?: SortDirection;
}

export interface UseTableSortOptions {
  columns: ColumnSortSpec[];
  /** Used when one page hosts several tables, e.g. the tabs of `/users/:id`. */
  paramPrefix?: string;
  onChange?: () => void;
}

export interface UseTableSortResult extends SortState {
  toggleSort: (key: string) => void;
  resetSort: () => void;
  /** Spread straight into query parameters; empty while in the default order. */
  sortParams: { sortBy?: string; sortOrder?: SortDirection };
  isSortable: (key: string) => boolean;
  getColumn: (key: string) => ColumnSortSpec | undefined;
}

export interface AppliedListSort {
  sortBy: string | null;
  sortOrder: SortDirection | null;
}

/**
 * Header indicator must follow the sort the API actually applied (contract §4 / FR-025),
 * not the params the client just sent.
 */
export function resolveHeaderSort(
  tableSort: Pick<UseTableSortResult, 'sortBy' | 'sortOrder'>,
  applied?: AppliedListSort | null,
): { activeKey: string | null; direction: SortDirection | null } {
  if (tableSort.sortBy) {
    return {
      activeKey: applied?.sortBy ?? tableSort.sortBy,
      direction: applied?.sortOrder ?? tableSort.sortOrder,
    };
  }
  return {
    activeKey: applied?.sortBy ?? null,
    direction: applied?.sortOrder ?? null,
  };
}

function paramNames(prefix?: string) {
  if (!prefix) return { sort: 'sort', dir: 'dir' };
  return { sort: `${prefix}Sort`, dir: `${prefix}Dir` };
}

export function useTableSort({
  columns,
  paramPrefix,
  onChange,
}: UseTableSortOptions): UseTableSortResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const names = useMemo(() => paramNames(paramPrefix), [paramPrefix]);

  const columnMap = useMemo(() => {
    const map = new Map<string, ColumnSortSpec>();
    for (const column of columns) map.set(column.key, column);
    return map;
  }, [columns]);

  // An unknown key in the URL (hand-edited address) is ignored and falls back to
  // the default order, without throwing and without a technical message (FR-018).
  const state = useMemo<SortState>(() => {
    const rawKey = searchParams.get(names.sort);
    if (!rawKey || !columnMap.has(rawKey)) {
      return { sortBy: null, sortOrder: null };
    }
    const rawDir = (searchParams.get(names.dir) || '').toLowerCase();
    const fallback = columnMap.get(rawKey)?.defaultDirection ?? 'DESC';
    const sortOrder: SortDirection =
      rawDir === 'asc' ? 'ASC' : rawDir === 'desc' ? 'DESC' : fallback;
    return { sortBy: rawKey, sortOrder };
  }, [searchParams, names, columnMap]);

  const writeState = useCallback(
    (next: SortState) => {
      const params = new URLSearchParams(searchParams);

      if (next.sortBy && next.sortOrder) {
        params.set(names.sort, next.sortBy);
        params.set(names.dir, next.sortOrder.toLowerCase());
      } else {
        params.delete(names.sort);
        params.delete(names.dir);
      }

      // Once the order changes, page 1 is the only view that still means
      // anything (FR-007).
      params.delete('page');

      // `replace` keeps a run of sort clicks out of history: Back should return
      // to the previous page, not step back through each sort.
      setSearchParams(params, { replace: true });
      onChange?.();
    },
    [searchParams, setSearchParams, names, onChange],
  );

  const toggleSort = useCallback(
    (key: string) => {
      const column = columnMap.get(key);
      if (!column) return;
      writeState(cycleSortState(state, key, column.defaultDirection ?? 'DESC'));
    },
    [columnMap, state, writeState],
  );

  const resetSort = useCallback(() => {
    writeState({ sortBy: null, sortOrder: null });
  }, [writeState]);

  const sortParams = useMemo(
    () =>
      state.sortBy && state.sortOrder
        ? { sortBy: state.sortBy, sortOrder: state.sortOrder }
        : {},
    [state],
  );

  const isSortable = useCallback((key: string) => columnMap.has(key), [columnMap]);
  const getColumn = useCallback((key: string) => columnMap.get(key), [columnMap]);

  return {
    ...state,
    toggleSort,
    resetSort,
    sortParams,
    isSortable,
    getColumn,
  };
}

export default useTableSort;
